import {
  LimitFormContent,
  LimitFormContentLayout,
  PerMemberFulfillmentLimit,
} from '@occc/feat-limits';
import { FulfillmentWindow } from '@occc/feat-constraints';
import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { Box, FormHelperText, Stack } from '@mui/material';

import { Offer_Engagement_Type, OfferFilter, OfferFragment } from '@occc/graphql';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { Button, Divider, LoadingOverlay, PublicOutlinedIcon } from '@occc-app/design-system';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import { configureFormCustomDataSubtitle } from '@occc-app/offerHelpers/copies';
import { OfferType } from '@occc-app/offerValidationService/types';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';

import { getDefaultSegment, MIN_SEGMENTS_LENGTH, NO_AWARD_SEGMENT_ERROR } from './constants';
import { SegmentConfiguration } from './components/SegmentConfiguration';
import { GamePreview } from './components/GamePreview';
import { PurchasableOfferSelector } from './components/PurchasableOfferSelector';
import { GameConfiguration } from './components/GameConfiguration';

import { validateConfiguration } from '../validation';
import { hasAwardSegment } from '../utils';
import { GameConfigureFormValues } from '../types';
import { CampaignComponentFooterActions } from '../../CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '../../CampaignComponents/components/CampaignComponentReadOnlyFooterActions';

interface ConfigureFormProps {
  onSubmit: (values: GameConfigureFormValues, form: FormApi<GameConfigureFormValues>) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: Partial<GameConfigureFormValues>;
  getOffersFilterMaker(engagementTypes: Offer_Engagement_Type[]): () => OfferFilter;
  loading: boolean;
  currentFlowType: Flow_Types;
  offerType: OfferType;
  onNextStepButtonClick?: () => void;
  isFormEditable: boolean;
}

export type Offer = Pick<OfferFragment, 'id' | 'name' | 'engagementType'>;

export const ConfigureForm = (props: ConfigureFormProps) => {
  const {
    initialValues,
    loading,
    currentFlowType,
    offerType,
    isFormEditable,
    onSubmit,
    onCancelClick,
    getOffersFilterMaker,
    onNextStepButtonClick,
  } = props;

  const renderForm: FormRenderProps<GameConfigureFormValues>['render'] = React.useCallback(
    ({
      handleSubmit,
      submitting,
      submitFailed,
      values,
      dirty,
      form,
    }: FormRenderProps<GameConfigureFormValues>) => {
      const handleAddSegment = () => form.mutators.push('segments', getDefaultSegment());

      const handleRemoveSegment = (index: number) => {
        form.mutators.remove('segments', index);
      };

      // TODO consider better approach
      const handleSegmentTypeChanged = (index: number) => {
        const segment = { ...values.segments[index] };
        segment.awardPoints = null;
        segment.awardOffer = null;
        segment.awardOfferExpirationDays = null;
        segment.icon = null;
        return form.mutators.update('segments', index, segment);
      };

      const isMinSegmentsErrorVisible =
        submitFailed && (values.segments?.length ?? 0) < MIN_SEGMENTS_LENGTH;
      const isNoAwardSegmentErrorVisible = submitFailed && !hasAwardSegment(values.segments ?? []);

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          onCancel={() => onCancelClick(dirty)}
          currentFlowType={currentFlowType}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={onCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
            <OfferEarningMechanismConfigurationSection>
              <Stack spacing={2}>
                {offerType === OfferType.VoucheredGame && (
                  <PurchasableOfferSelector
                    makeOffersFilter={getOffersFilterMaker([Offer_Engagement_Type.Purchaseable])}
                  />
                )}

                {values.gameType && (
                  <Stack direction='row' spacing={3} justifyContent='space-between'>
                    <GameConfiguration gameType={values.gameType} />
                    {Boolean(values.segments?.length) && (
                      <Box
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                        py={3}
                        flex={1}
                      >
                        <GamePreview
                          gameType={values.gameType}
                          segments={values.segments}
                          spinWheelGameConfig={values.spinWheelGameConfig}
                          isGameplayAmountDisplayed={offerType === OfferType.VoucheredGame}
                        />
                      </Box>
                    )}
                  </Stack>
                )}

                <Divider />
                <Stack divider={<Divider />} spacing={2}>
                  {values.segments?.map((segment, index) => (
                    <SegmentConfiguration
                      index={index}
                      key={segment.id}
                      onRemove={() => handleRemoveSegment(index)}
                      onTypeChanged={() => handleSegmentTypeChanged(index)}
                      makeOffersFilter={getOffersFilterMaker([Offer_Engagement_Type.Vouchered])}
                      segmentType={segment.type}
                      isDeleteButtonDisabled={!isFormEditable}
                    />
                  ))}
                </Stack>
                <Divider />
                {isMinSegmentsErrorVisible && (
                  <FormHelperText error>
                    At least {MIN_SEGMENTS_LENGTH} segments should be configured
                  </FormHelperText>
                )}
                {isNoAwardSegmentErrorVisible && (
                  <FormHelperText error>{NO_AWARD_SEGMENT_ERROR}</FormHelperText>
                )}
                <Button
                  disabled={!isFormEditable}
                  sx={{ alignSelf: 'center' }}
                  onClick={handleAddSegment}
                  variant='contained'
                >
                  + Add another segment
                </Button>
              </Stack>
            </OfferEarningMechanismConfigurationSection>

            <OfferConfigurationSection title='Limits'>
              <LimitFormContentLayout
                title='Global Fulfillment Limit'
                description='Global Fulfillment Limit'
                action={null}
                open={true}
                avatar={PublicOutlinedIcon}
              >
                <LimitFormContent name='awardCount.global' />
              </LimitFormContentLayout>

              <PerMemberFulfillmentLimit
                open={values?.awardCount?.membership?.isEnabled}
                showAvatar
              />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Requirements'>
              <FulfillmentWindow values={values} />
            </OfferConfigurationSection>

            <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
              <CustomData values={values.customData} subtitle={configureFormCustomDataSubtitle} />
            </OfferConfigurationSection>
          </OfferConfigurationWizardFormLayout>
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      onCancelClick,
      onNextStepButtonClick,
      offerType,
      getOffersFilterMaker,
    ],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<GameConfigureFormValues>
          onSubmit={onSubmit}
          render={renderForm}
          mutators={{
            ...arrayMutators,
          }}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
